import { useCallback, useEffect, useState } from "react";
import { submitFeedback, fetchFeedback, saveShow } from "../api/client";
import { Link } from "react-router-dom";
import Badge from "./ui/Badge";
import Button from "./ui/Button";
import Card from "./ui/Card";
import FeedbackButtons from "./ui/FeedbackButtons";
import PosterFrame from "./ui/PosterFrame";
import { BETA_ANALYTICS_EVENTS, trackBetaEvent } from "../utils/analytics";
import {
  getTrailerUrl,
  getWatchUrl,
  listText,
  normalizeTags,
  showPath,
} from "../utils/showDisplay";

export default function PitchCard({ show, guestId, userId = null }) {
  const [feedbackStatus, setFeedbackStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [saveStatus, setSaveStatus] = useState("");
  const [isSavingShow, setIsSavingShow] = useState(false);

  const identityId = userId || guestId;
  const identityType = userId ? "user" : "guest";

  const {
    title,
    id,
    platform,
    genres = [],
    moods = [],
    cultural_tags = [],
    laugh_factor,
    commitment_level,
    content_flags = {},
    pitch = {},
  } = show || {};

  useEffect(() => {
    const fetchExistingFeedback = async () => {
      if (!identityId || !show?.title) return;

      try {
        const res = await fetchFeedback(identityId, show.title, identityType);
        if (res?.feedback) {
          setSelectedFeedback(res.feedback);
        }
      } catch (err) {
        console.error("⚠️ Error fetching previous feedback:", err.message);
      }
    };

    fetchExistingFeedback();
  }, [identityId, identityType, show?.title]);

  const handleFeedback = useCallback(
    async (type) => {
      if (!identityId || !show?.title) {
        setFeedbackStatus("❌ Missing session or show title");
        return;
      }

      const feedback = type || "nah";

      try {
        setIsSubmitting(true);
        setFeedbackStatus(null);

        const response = await submitFeedback({
          guest_id: identityType === "guest" ? identityId : null,
          user_id: identityType === "user" ? identityId : null,
          show_title: show.title,
          feedback,
          genres: genres || [],
          moods: moods || [],
          cultural_tags: cultural_tags || [],
          laugh_factor: laugh_factor || null,
          commitment_level: commitment_level || null,
        });

        const { message } = response;
        setFeedbackStatus(message);
        setSelectedFeedback(feedback);
        trackBetaEvent(BETA_ANALYTICS_EVENTS.FEEDBACK_SUBMITTED, {
          feedback,
          identity_type: identityType,
          show_id: id || null,
        });
      } catch (err) {
        setFeedbackStatus("❌ Error saving feedback");
        console.error("Feedback error:", err.message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      commitment_level,
      cultural_tags,
      genres,
      id,
      identityId,
      identityType,
      laugh_factor,
      moods,
      show?.title,
    ]
  );

  const handleSaveShow = useCallback(async () => {
    if (!userId || !id) return;

    try {
      setIsSavingShow(true);
      setSaveStatus("");
      await saveShow(id);
      setSaveStatus("Saved");
    } catch (err) {
      setSaveStatus("Could not save");
      console.error("Save show error:", err.message);
    } finally {
      setIsSavingShow(false);
    }
  }, [id, userId]);

  if (!show) return null;

  const {
    hook,
    why_youll_love_it,
    commitment,
    empress_hook,
    binge_strategy,
    safety_note,
  } = pitch;

  const tags = normalizeTags([...genres.slice(0, 2), ...cultural_tags.slice(0, 2)]);
  const activeWarnings = Object.entries(content_flags || {})
    .filter(([_, flagged]) => flagged)
    .map(([flag]) => flag.replace(/_/g, " "));
  const matchLabel = show.match_score || show.score || show.final_score || null;
  const watchUrl = getWatchUrl(show);
  const trailerUrl = getTrailerUrl(show);

  return (
    <Card interactive className="overflow-hidden p-4 sm:p-5">
      <div className="grid gap-5 lg:grid-cols-[180px_1fr_180px]">
        <PosterFrame show={show} className="aspect-[2/3] min-h-64 lg:min-h-0" />

        <div className="min-w-0">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {matchLabel && <Badge variant="purple">{matchLabel}% match</Badge>}
            <Badge variant="gold">{platform || "Unknown"}</Badge>
            {commitment_level && <Badge variant="neutral">{commitment_level}</Badge>}
          </div>

          <h3 className="font-display text-3xl font-bold leading-tight text-hookd-cream sm:text-4xl">
            {title || "Untitled"}
          </h3>

          <p className="mt-2 text-sm text-hookd-muted">
            {listText(genres, "Genres not set")} {moods.length ? `• ${listText(moods)}` : ""}
          </p>

          <div className="mt-5 space-y-4 text-sm leading-6">
            {(hook || empress_hook) && (
              <section>
                <p className="mb-1 text-xs font-bold uppercase text-hookd-gold">The Hook</p>
                <p className="text-hookd-cream">{hook || empress_hook}</p>
              </section>
            )}

            <div className="grid gap-4 md:grid-cols-3">
              {why_youll_love_it && (
                <section className="border-l border-hookd-line pl-4">
                  <p className="mb-1 text-xs font-bold uppercase text-hookd-gold">
                    Why You'll Love It
                  </p>
                  <p className="text-hookd-muted">{why_youll_love_it}</p>
                </section>
              )}
              {(binge_strategy || commitment) && (
                <section className="border-l border-hookd-line pl-4">
                  <p className="mb-1 text-xs font-bold uppercase text-hookd-gold">
                    Binge Strategy
                  </p>
                  <p className="text-hookd-muted">{binge_strategy || commitment}</p>
                </section>
              )}
              <section className="border-l border-hookd-line pl-4">
                <p className="mb-1 text-xs font-bold uppercase text-hookd-gold">Vibe</p>
                <div className="flex flex-wrap gap-2">
                  {tags.length ? tags.map((tag) => <Badge key={tag}>{tag}</Badge>) : <Badge variant="neutral">Curated</Badge>}
                </div>
              </section>
            </div>

            {(safety_note || activeWarnings.length > 0) && (
              <div className="rounded-hookd border border-hookd-gold/25 bg-hookd-gold/10 p-3 text-hookd-muted">
                <span className="font-bold text-hookd-gold">Safety Note: </span>
                {safety_note || activeWarnings.join(", ")}
              </div>
            )}
          </div>
        </div>

        <div className="grid content-start gap-3">
          <Button
            as="a"
            href={watchUrl}
            target="_blank"
            rel="noreferrer"
            variant="accent"
            className="w-full"
            onClick={() =>
              trackBetaEvent(BETA_ANALYTICS_EVENTS.SHOW_CLICKED, {
                action: "watch_now",
                show_id: id || null,
                surface: "recommendation_card",
              })
            }
          >
            Watch Now
          </Button>
          <Button
            as={Link}
            to={showPath(show)}
            state={{ show }}
            variant="outline"
            className="w-full"
            onClick={() =>
              trackBetaEvent(BETA_ANALYTICS_EVENTS.SHOW_CLICKED, {
                action: "view_details",
                show_id: id || null,
                surface: "recommendation_card",
              })
            }
          >
            View Details
          </Button>
          <Button
            as="a"
            href={trailerUrl}
            target="_blank"
            rel="noreferrer"
            variant="ghost"
            className="w-full"
            onClick={() =>
              trackBetaEvent(BETA_ANALYTICS_EVENTS.SHOW_CLICKED, {
                action: "trailer",
                show_id: id || null,
                surface: "recommendation_card",
              })
            }
          >
            Trailer
          </Button>
          {userId && id && (
            <Button
              type="button"
              onClick={handleSaveShow}
              loading={isSavingShow}
              variant="outline"
              className="w-full"
            >
              {saveStatus === "Saved" ? "Saved" : "Save Show"}
            </Button>
          )}
        </div>
      </div>

      <div className="mt-5 border-t border-hookd-line pt-5">
        <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <p className="mb-3 text-xs font-bold uppercase text-hookd-gold">
              Help me get it right
            </p>
            <FeedbackButtons
              selected={selectedFeedback}
              disabled={isSubmitting || !identityId}
              onFeedback={handleFeedback}
            />
          </div>
          <div className="text-sm text-hookd-muted lg:text-right">
            {feedbackStatus && <p>{feedbackStatus}</p>}
            {saveStatus && <p>{saveStatus}</p>}
            {!identityId && <p>Session not available.</p>}
          </div>
        </div>
      </div>
    </Card>
  );
}
